import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { cabinetApi } from '../api'
import { useOrg } from '../org'
import { useT } from '../i18n'

const fieldCls =
  'rounded-lg border border-hull bg-trench px-2.5 py-1.5 text-sm text-foam focus:border-sonar focus:outline-none'

export function OrgMembers() {
  const t = useT()
  const qc = useQueryClient()
  const { activeOrg } = useOrg()
  const isOwner = activeOrg?.role === 'owner'
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('user')
  const [err, setErr] = useState<string | null>(null)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['cabinet', 'members'],
    queryFn: () => cabinetApi.orgMembers(),
    enabled: isOwner,
  })

  if (!isOwner) {
    return (
      <p className="text-sm text-mist">
        {t('settings.members.ownerOnly')}
      </p>
    )
  }
  if (isLoading) return <p className="text-sm text-mist">{t('common.loading')}</p>
  if (isError) return <p className="text-sm text-coral">{t('settings.members.loadFailed')}</p>

  const invalidate = () => qc.invalidateQueries({ queryKey: ['cabinet', 'members'] })

  const add = async (e: React.FormEvent) => {
    e.preventDefault()
    setErr(null)
    try {
      await cabinetApi.addMember(email.trim().toLowerCase(), role)
      setEmail('')
      invalidate()
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : t('map.load.error'))
    }
  }

  return (
    <div className="max-w-lg space-y-4">
      <table className="w-full text-sm">
        <thead className="eyebrow text-left">
          <tr>
            <th className="py-1 font-medium">Email</th>
            <th className="py-1 font-medium">{t('settings.members.role')}</th>
            <th className="py-1"></th>
          </tr>
        </thead>
        <tbody>
          {(data?.members ?? []).map(m => (
            <tr key={m.user_id} className="border-t border-hull">
              <td className="py-1.5 text-foam">{m.email}</td>
              <td className="py-1.5">
                <select
                  value={m.role}
                  onChange={async e => {
                    // Отказ (например, last_owner) обязан быть виден: без catch
                    // выбор молча откатывался, и смена роли выглядела сломанной.
                    setErr(null)
                    try {
                      await cabinetApi.setMemberRole(m.user_id, e.target.value)
                      invalidate()
                    } catch (e2) {
                      setErr(e2 instanceof Error ? e2.message : t('map.load.error'))
                    }
                  }}
                  className="rounded border border-hull bg-trench px-1.5 py-0.5 text-sm text-foam focus:border-sonar focus:outline-none"
                >
                  <option value="owner">owner</option>
                  <option value="user">user</option>
                </select>
              </td>
              <td className="py-1.5 text-right">
                <button
                  type="button"
                  onClick={async () => {
                    await cabinetApi.removeMember(m.user_id)
                    invalidate()
                  }}
                  className="text-xs text-coral hover:underline"
                >
                  {t('common.remove')}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <form onSubmit={add} className="flex items-end gap-2">
        <div className="flex-1">
          <label className="eyebrow mb-1.5">{t('settings.members.addByEmail')}</label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="user@example.ru"
            className={`w-full ${fieldCls} placeholder:text-mist`}
          />
        </div>
        <select value={role} onChange={e => setRole(e.target.value)} className={fieldCls}>
          <option value="user">user</option>
          <option value="owner">owner</option>
        </select>
        <button
          type="submit"
          disabled={!email.trim()}
          className="rounded-lg bg-gradient-to-b from-sonar to-sonar-dim px-3 py-1.5 text-sm font-semibold text-abyss shadow-sonar disabled:opacity-40"
        >
          {t('settings.members.add')}
        </button>
      </form>
      {err && <p className="text-sm text-coral">{err}</p>}
    </div>
  )
}
